import type { SlotCustomization } from '@features/customization-session/types';

import { FileText, RefreshCw } from 'lucide-react';

import { Button } from '@shared/components/ui/button';

type Props = {
  customizations: SlotCustomization[];
  /** Fetches (or fetches again) the PDF of a personalization from the shop. */
  onFetch: (customization: number) => void;
  disabled?: boolean;
};

/**
 * The personalizations of a product, as a footer of its card: one line each, with the PDF link when fetched and the
 * button to fetch it from the shop. Fetching again is an icon (the card stays narrow); the first fetch keeps its label.
 */
export const SlotPdfs = ({ customizations, onFetch, disabled = false }: Props) => {
  if (customizations.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-col gap-1 border-t border-border pt-2">
      {customizations.map((customization) => (
        <div className="flex min-h-10 items-center gap-3 text-sm" key={customization.id}>
          <span
            className="min-w-0 flex-1 truncate text-muted-foreground"
            title={`Personnalisation ${customization.customizationId}`}
          >
            Perso {customization.customizationId}
          </span>
          {customization.pdfUrl && (
            <a
              className="flex shrink-0 items-center gap-1 font-medium text-primary underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              href={customization.pdfUrl}
              onClick={(event) => event.stopPropagation()}
              rel="noreferrer"
              target="_blank"
            >
              <FileText className="size-4" />
              PDF
            </a>
          )}
          <Button
            aria-label={
              customization.pdfUrl
                ? `Récupérer à nouveau le PDF de la personnalisation ${customization.customizationId}`
                : undefined
            }
            className="shrink-0"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onFetch(customization.id);
            }}
            size={customization.pdfUrl ? 'icon' : 'sm'}
            title={customization.pdfUrl ? 'Récupérer à nouveau' : undefined}
            variant="outline"
          >
            <RefreshCw />
            {!customization.pdfUrl && 'Récupérer le PDF'}
          </Button>
        </div>
      ))}
    </div>
  );
};
